import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/admin`,
});

// Request interceptor to add the admin token
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
adminApi.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // If unauthorized, redirect to login
      localStorage.removeItem('adminToken');
      window.location.href = '/admin';
    }
    console.error('Admin API Error:', error);
    return Promise.reject(error);
  }
);

export const fetchAdminData = () => adminApi.get('/dashboard');

export const updateLeadStatus = (leadId: number, processed: boolean) => 
  adminApi.put(`/leads/${leadId}/status`, { processed });

// CRUD functions for categories
export const fetchCategories = () => adminApi.get('/categories');
export const addCategory = (categoryData: { name: string }) => adminApi.post('/categories', categoryData);
export const updateCategory = (id: number, categoryData: { name: string }) => adminApi.put(`/categories/${id}`, categoryData);
export const deleteCategory = (id: number) => adminApi.delete(`/categories/${id}`);

// CRUD functions for projects
export const fetchProjects = () => adminApi.get('/projects');
export const addProject = (projectData: any) => adminApi.post('/projects', projectData);
export const updateProject = (id: number, projectData: any) => adminApi.put(`/projects/${id}`, projectData);
export const deleteProject = (id: number) => adminApi.delete(`/projects/${id}`);

// CRUD functions for metrics
export const addMetric = (projectId: number, metricData: any) => adminApi.post(`/projects/${projectId}/metrics`, metricData);
export const updateMetric = (metricId: number, metricData: any) => adminApi.put(`/metrics/${metricId}`, metricData);
export const deleteMetric = (metricId: number) => adminApi.delete(`/metrics/${metricId}`);

// CRUD functions for metric data
export const addMetricData = (metricId: number, dataPoint: any) => adminApi.post(`/metrics/${metricId}/data`, dataPoint);
export const updateMetricData = (dataId: number, dataPoint: any) => adminApi.put(`/metric-data/${dataId}`, dataPoint);
export const deleteMetricData = (dataId: number) => adminApi.delete(`/metric-data/${dataId}`);

// Image upload function
export const uploadChartImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('chartImage', file);
  
  try {
    const response = await axios.post(`${API_BASE_URL}/upload-chart`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.imageUrl;
  } catch (error) {
    console.error('Error uploading chart image:', error);
    throw error;
  }
};

// Calendly link functions
export const getCalendlyLink = () => adminApi.get('/calendly-link');
export const updateCalendlyLink = (link: string) => adminApi.put('/calendly-link', { link });

export default adminApi;